// Feature 009: Deal Operating System
// Custom hook for keyboard shortcuts
import { useEffect, useCallback } from 'react';

/**
 * Custom hook for handling keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to callbacks
 * @param {boolean} enabled - Whether shortcuts are enabled
 *
 * Key format examples:
 * - 'Escape' - single key
 * - 'Enter' - Enter key
 * - 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight' - arrow keys
 * - 'ctrl+s' - Ctrl+S
 * - 'cmd+k' - Cmd+K (Mac) / Ctrl+K (Windows)
 */
const useKeyboardShortcuts = (shortcuts, enabled = true) => {
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs
    const tagName = event.target.tagName.toLowerCase();
    const isEditable = event.target.isContentEditable;
    const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isEditable;

    // Build key combination string
    const parts = [];
    if (event.ctrlKey) parts.push('ctrl');
    if (event.metaKey) parts.push('cmd');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');

    const key = event.key;
    parts.push(key);

    const combo = parts.join('+');
    const simpleKey = key; // For single-key shortcuts like Escape

    // Check for matching shortcuts
    Object.entries(shortcuts).forEach(([shortcut, callback]) => {
      // Normalize shortcut
      const normalizedShortcut = shortcut.toLowerCase();
      const normalizedCombo = combo.toLowerCase();
      const normalizedKey = simpleKey.toLowerCase();

      // Allow Escape to work even in inputs (for closing modals)
      const allowInInput = normalizedShortcut === 'escape';

      if (isInput && !allowInInput) return;

      // Match either the full combo or just the key
      if (normalizedShortcut === normalizedCombo || normalizedShortcut === normalizedKey) {
        event.preventDefault();
        callback(event);
      }
    });
  }, [shortcuts, enabled]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);
};

export default useKeyboardShortcuts;
